import { Upload as UploadIcon } from "@mui/icons-material";
import { Button, Grid, Stack, Typography } from "@mui/material";
import React, { FC, PropsWithChildren, useEffect, useState } from "react";
import { ImageFrame, ImageFrameImage } from './ImageFrame.styles';
import { FormCheckBox } from "src/components/FormPart/FormCheckBox";
import { FormColorInput } from "src/components/FormPart/FormColorInput";
import { FormFileUploadButton } from "src/components/FormPart/FormFileUploadButton";
import { FormDropdown } from "src/components/FormPart/FormDropdown";
import { FormNumberInput } from "src/components/FormPart/FormNumberInput";
import { FormTextInput } from "src/components/FormPart/FormTextInput";
import { FormComponentProps } from "src/components/FormPart/types";
import { ApplicationFormData, ApplicationSettingsFormData } from "../../types";
import { validateBookingRedirectSeconds } from "../../helpers/builders";
import { UseCaseSelector } from "src/components/UseCaseSelector/UseCaseSelector";
import { SubUseCaseSelector } from "src/components/UseCaseSelector/SubUseCaseSelector";
import { useGetUseCasesApi, useGetVerticalsApi, UseCasesResponse } from "src/api/campaignRegistry";
import { buildDefaultCampaignInfoPayload } from "src/modules/companies/helpers/campaignDefaults";
import { processEnumOptions } from "src/modules/companies/helpers/processEnumOptions";

// Past this the abandonment pass no longer reads the funnel at all, so a later checkpoint parses and then
// never fires.
const MAX_REMINDER_HOURS = 696;

const COMPANY_NAME_PLACEHOLDER = "{{companyName}}";

/**
 * The value is rendered as a frame on the last signup screen, so a mistyped one is a blank screen for every
 * client of the application. Only the scheme is ruled on: the host is whichever scheduler the team uses.
 */
const validateBookingUrl = (value?: string): string | true => {
  if (!value || value.trim().length === 0) return true;

  try {
    return new URL(value.trim()).protocol === "https:" || "The booking link must start with https://";
  } catch {
    return "Enter a full booking link, for example https://calendly.com/your-team/onboarding";
  }
};

type CreateAdminApplicationFormProps = object;

export const CreateAdminApplicationForm: FC<
  FormComponentProps<ApplicationFormData<ApplicationSettingsFormData>, CreateAdminApplicationFormProps>
> = (props) => {
  const { form } = props;

  const [useCasesData, setUseCasesData] = useState<UseCasesResponse>({});
  const [verticals, setVerticals] = useState<{ value: string; title: string }[]>([]);
  const getUseCases = useGetUseCasesApi();
  const getVerticals = useGetVerticalsApi();

  const { id } = form.watch();
  const { logo, favicon } = form.watch("settings");
  const selfServiceEnabled = form.watch("settings.selfService.enabled");
  const defaultUseCase = form.watch("defaultUseCase");
  const defaultSubUseCases = form.watch("defaultSubUseCases");

  /**
   * How many kinds of message the registry insists on for the chosen use case. The funnel no longer asks
   * the client, so this form is the only place left where a campaign short of them can be seen coming.
   */
  const requiredSubUseCases = useCasesData[defaultUseCase]?.minSubUsecases ?? 0;
  const missingSubUseCases = requiredSubUseCases - (defaultSubUseCases?.length ?? 0);

  /**
   * What the funnel writes for a company when these fields are empty, shown as the placeholder of each.
   * A placeholder rather than a value on purpose: written in, it would be stored, and the stored copy stops
   * following the use case above — the opt-in message carries the marketing disclosure only for a marketing
   * one — and stops following any later change to the wording the carriers are given.
   *
   * Read out of the wizard's own builder rather than spelled out again: the same sentences reach the
   * carriers from the wizard, from the funnel and from here, and a fourth copy would be the one nobody
   * updates.
   */
  const writtenSamples = buildDefaultCampaignInfoPayload(
    COMPANY_NAME_PLACEHOLDER,
    undefined,
    undefined,
    defaultUseCase ?? "",
    defaultSubUseCases ?? [],
  );

  // Load use cases data
  useEffect(() => {
    const fetchUseCases = async () => {
      try {
        const response = await getUseCases();
        if (response.data && Object.keys(response.data).length > 0) {
          setUseCasesData(response.data);
        } else {
          setUseCasesData({});
        }
      } catch (error) {
        console.error('Failed to fetch use cases:', error);
        setUseCasesData({});
      }
    };

    fetchUseCases();
  }, [getUseCases]);

  // The list the industry below is chosen from; unread, it offers only "Ask during signup".
  useEffect(() => {
    let cancelled = false;
    void getVerticals()
      .then(({ data }) => {
        if (cancelled) return;
        setVerticals(processEnumOptions(data).map((o) => ({ value: o.value, title: o.label })));
      })
      .catch(() => undefined);

    return () => {
      cancelled = true;
    };
  }, [getVerticals]);

  return (
    <Grid container spacing={1}>
      <Grid item xs={12}>
        <FormTextInput
          form={form}
          fieldName="name"
          label="Application Name"
          placeholder="Enter Application Name"
          validation={{
            required: "Application Name is required",
          }}
        />
      </Grid>

      <Grid item xs={12}>
        <FormTextInput
          form={form}
          fieldName="domain"
          label="Domain Name"
          placeholder="Enter Domain Name"
          validation={{
            required: "Domain Name is required",
          }}
        />
      </Grid>

      <Grid item xs={12}>
        <UseCaseSelector
          form={form}
          label="Use Case"
          useCaseField="defaultUseCase"
          useCasesData={useCasesData}
          validation={{
            required: "UseCase is required",
          }}
        />
      </Grid>

      <Grid item xs={12}>
        <SubUseCaseSelector
          form={form}
          useCaseField="defaultUseCase"
          subUseCasesField="defaultSubUseCases"
          useCasesData={useCasesData}
        />
        {/* The selector counts up to the maximum and says nothing about the minimum. */}
        {missingSubUseCases > 0 && (
          <Typography variant="caption" color="error" display="block" mt={1}>
            {`Choose at least ${requiredSubUseCases} ${requiredSubUseCases === 1 ? "kind" : "kinds"} of message — the carriers refuse this campaign with fewer.`}
          </Typography>
        )}
      </Grid>

      <Grid item xs={6}>
        <ImagePreview image={logo} noImageText="No Logo">
          <FormFileUploadButton
            form={form}
            fieldName="settings.logo"
            label="Logo"
            startIcon={<UploadIcon />}
            validation={{
              required: "Logo is required",
            }}
            dropAreaNote="Allowed formats: *.jpg, *.jpeg, *.png"
            accept="image/jpeg,image/png"
          />
        </ImagePreview>
      </Grid>

      <Grid item xs={6}>
        <ImagePreview image={favicon} noImageText="No Favicon">
          <FormFileUploadButton
            form={form}
            fieldName="settings.favicon"
            label="Favicon"
            startIcon={<UploadIcon />}
            validation={{
              required: "Favicon is required",
            }}
            dropAreaNote="Allowed formats: *.jpg, *.jpeg, *.png, *.ico"
            accept="image/jpeg,image/png,image/x-icon"
          />
        </ImagePreview>
      </Grid>

      <Grid item xs={12}>
        <FormNumberInput
          form={form}
          fieldName="priceForSMS"
          label="Credits per SMS"
          placeholder="Enter Credits per SMS"
          validation={{
            required: "Credits per SMS is required",
          }}
          format={{
            maximumFractionDigits: 0,
            minimumFractionDigits: 0,
          }}
          min={0}
        />
      </Grid>

      <Grid item xs={12}>
        <FormNumberInput
          form={form}
          fieldName="priceForMMS"
          label="Credits per MMS"
          placeholder="Enter Credits per MMS"
          validation={{
            required: "Credits per MMS is required",
          }}
          format={{
            maximumFractionDigits: 0,
            minimumFractionDigits: 0,
          }}
          min={0}
        />
      </Grid>

      <Grid item xs={12}>
        <FormNumberInput
          form={form}
          fieldName="priceForLookup"
          label="Credits per Number Lookup"
          placeholder="Enter Credits per Number Lookup"
          validation={{
            required: "Credits per Number Lookup is required",
          }}
          format={{
            maximumFractionDigits: 0,
            minimumFractionDigits: 0,
          }}
          min={0}
        />
      </Grid>

      <Grid item xs={12}>
        <FormNumberInput
          form={form}
          fieldName="priceForAlert"
          label="Credits per Alert"
          placeholder="Enter Credits per Alert"
          validation={{
            required: "Credits per Alert is required",
          }}
          format={{
            maximumFractionDigits: 0,
            minimumFractionDigits: 0,
          }}
          min={0}
        />
      </Grid>

      <Grid item xs={12}>
        <FormColorInput
          form={form}
          fieldName="settings.primaryButtonColor"
          label="Primary Button Color"
          placeholder="Enter Primary Button Color"
          validation={{
            required: "Primary Button Color is required",
          }}
        />
      </Grid>

      <Grid item xs={12}>
        <FormColorInput
          form={form}
          fieldName="settings.primaryButtonHoverColor"
          label="Primary Button Hover Color"
          placeholder="Enter Primary Button Hover Color"
          validation={{
            required: "Primary Button Hover Color is required",
          }}
        />
      </Grid>

      <Grid item xs={12}>
        <FormColorInput
          form={form}
          fieldName="settings.primaryTableHeaderColor"
          label="Primary Table Header Color"
          placeholder="Enter Primary Table Header Color"
          validation={{
            required: "Primary Table Header Color is required",
          }}
        />
      </Grid>

      <Grid item xs={12}>
        <Typography variant="subtitle1" fontWeight={700} mt={2}>
          Self-Service Signup
        </Typography>
        <Typography variant="body2" color="text.secondary">
          When off, the public signup pages for this application are unavailable.
        </Typography>
      </Grid>

      <Grid item xs={12}>
        <FormCheckBox
          form={form}
          fieldName="settings.selfService.enabled"
          label="Allow anyone to sign up on this application"
        />
      </Grid>

      {selfServiceEnabled && (
        <>
          <Grid item xs={12}>
            {/* Struck through next to "included" on the plan screen; zero hides the comparison entirely. */}
            <FormNumberInput
              form={form}
              fieldName="settings.selfService.localNumberPriceUsd"
              label="Local Number Strike Through Price, $"
              placeholder="Enter Strike Through Price"
              helperText="For marketing only, never charged. Leave at 0 to show the number as included without a strike through price."
              min={0}
            />
          </Grid>

          <Grid item xs={12}>
            <FormNumberInput
              form={form}
              fieldName="settings.selfService.freeCredits"
              label="Free Credits for a New Company"
              placeholder="Enter Free Credits"
              helperText="What a company created by the funnel starts with. Zero means an empty balance."
              format={{ maximumFractionDigits: 0, minimumFractionDigits: 0 }}
              min={0}
            />
          </Grid>

          <Grid item xs={12}>
            <FormDropdown
              form={form}
              fieldName="settings.selfService.vertical"
              label="Industry"
              placeholder="Select Industry"
              options={[{ value: "", title: "Ask during signup" }, ...verticals]}
              displayEmpty
              helperText="Optional. When set, every company created through signup is registered under this industry and the signup form doesn't ask for it. Leave empty to let each company choose its industry during signup."
            />
          </Grid>

          <Grid item xs={12}>
            <FormTextInput
              form={form}
              fieldName="settings.selfService.bookingUrl"
              label="Booking Link"
              placeholder="https://calendly.com/your-team/onboarding"
              helperText="A link to your scheduling service. Leave empty and the booking screen is skipped."
              validation={{ validate: validateBookingUrl }}
            />
          </Grid>

          <Grid item xs={12}>
            {/* A text input: the number input shows 0 for an emptied field, and empty here means no redirect. */}
            <FormTextInput
              form={form}
              fieldName="settings.selfService.bookingRedirectSeconds"
              label="Redirect After Booking (seconds)"
              helperText="How long the booking confirmation stays on screen before the client is taken to their account. 0 moves on at once; clear the field to keep the Continue button instead."
              validation={{ validate: validateBookingRedirectSeconds }}
            />
          </Grid>

          <Grid item xs={12}>
            <FormTextInput
              form={form}
              fieldName="settings.selfService.sampleMessage1"
              label="Sample Message 1"
              placeholder={writtenSamples.sample1}
              helperText="Left empty, the default message goes to the carriers."
              multiline
              minRows={2}
              shrinkLabel
            />
          </Grid>

          <Grid item xs={12}>
            <FormTextInput
              form={form}
              fieldName="settings.selfService.sampleMessage2"
              label="Sample Message 2"
              placeholder={writtenSamples.sample2}
              helperText="Left empty, the default message goes to the carriers."
              multiline
              minRows={2}
              shrinkLabel
            />
          </Grid>

          <Grid item xs={6}>
            <FormNumberInput
              form={form}
              fieldName="settings.selfService.firstReminderHours"
              label="First Reminder, hours"
              placeholder="24"
              helperText="Leave both empty to use the default 24 and 72 hours."
              format={{ maximumFractionDigits: 0, minimumFractionDigits: 0 }}
              min={1}
              max={MAX_REMINDER_HOURS}
            />
          </Grid>

          <Grid item xs={6}>
            <FormNumberInput
              form={form}
              fieldName="settings.selfService.secondReminderHours"
              label="Second Reminder, hours"
              placeholder="72"
              helperText="Leave empty to send one reminder only."
              format={{ maximumFractionDigits: 0, minimumFractionDigits: 0 }}
              min={1}
              max={MAX_REMINDER_HOURS}
            />
          </Grid>
        </>
      )}

      <Grid item xs={12}>
        <Button
          type="submit"
          disabled={form.formState.isSubmitting}
          variant="contained"
          color="secondary"
          size="large"
          fullWidth
        >
          {id ? "Update" : "Create"}
        </Button>
      </Grid>
    </Grid>
  );
};

const ImagePreview: FC<
  PropsWithChildren<{
    image: File | string | undefined | null;
    noImageText: string;
  }>
> = (props) => {
  const { image, noImageText, children } = props;

  return (
    <Stack spacing={1}>
      <ImageFrame>
        {image ? (
          <ImageFrameImage
            src={typeof image === "string" ? image : URL.createObjectURL(image)}
            alt=""
          />
        ) : (
          <Typography variant="caption">{noImageText}</Typography>
        )}
      </ImageFrame>
      <div>{children}</div>
    </Stack>
  );
};
